import merge from "lodash/merge";
import React, { InputHTMLAttributes } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useTheme } from "../ThemeContext";
import { Rounded } from "../theme/Rounded";
import { Spacing } from "../theme/Spacing";
import { Flex } from "./Flex";
import { FontSize, Text } from "./Text";
import { Tooltip } from "../utils/local-antd";
import { AtiraIcon } from "./AtiraIcon";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import RequiredSpan from "./RequiredSpan";
import { ThemeType } from "../theme/Theme";

// @ts-ignore
const StyledTextarea = styled.textarea.attrs((props: TextareaProps) => ({
  "data-testid": props.dataTestId,
}))<TextareaProps>`
  min-height: 4rem;
  max-height: ${(props) => props.maxHeight || "12rem"};
  max-width: 100%;
  min-width: 100%;
  outline: none;
  border-radius: ${Rounded.md};
  border: ${({ theme }) => `1px solid ${theme.lightgray}`};
  font-family: inherit;
  font-size: ${FontSize.m};

  &:disabled {
    background-color: ${({ theme }) => theme.darkerSub} !important;
    cursor: not-allowed;
  }
`;

const OptionalSpan = styled.span`
  color: ${({ theme }) => theme.darkerSub};
  font-size: ${FontSize.s};
`;

const StyledLabel = styled.label<{ color?: keyof ThemeType }>`
  cursor: pointer;
  color: ${({ theme, color }) => (color ? theme[color] : theme.black)};
  font-size: ${FontSize.m};
`;

const StyledInfo = styled(Tooltip)`
  cursor: help;
`;

interface TextareaProps extends InputHTMLAttributes<HTMLTextAreaElement> {
  title?: string;
  titleColor?: keyof ThemeType;
  id?: string;
  width?: string;
  height?: string;
  maxHeight?: string;
  rows?: number;
  border?: string;
  borderRadius?: keyof typeof Rounded;
  placeholder?: string;
  padding?: string;
  margin?: string;
  color?: string;
  errorMessage?: string;
  valid?: boolean;
  dataTestId?: string;
  info?: string;
}

export const Textarea: React.FC<TextareaProps> = (props) => {
  const { theme } = useTheme();
  //
  const { t } = useTranslation();

  return (
    <Flex flexDirection="column" gap="s" width={props.width || "100%"}>
      <Flex justifyContent="space-between">
        <Flex gap="s">
          {props?.info ? (
            <StyledInfo title={props.info}>
              <span>
                <AtiraIcon icon={faInfoCircle} color="main" size="sm" />
              </span>
            </StyledInfo>
          ) : null}
          {props.title ? (
            <StyledLabel htmlFor={props.id} color={props.titleColor}>
              {props.title}
              {props.required ? (
                <RequiredSpan />
              ) : (
                <OptionalSpan> ({t("common.optional")})</OptionalSpan>
              )}
            </StyledLabel>
          ) : null}
        </Flex>
        {props.maxLength || props.minLength ? (
          <Text fontSize="xs" color={"darkerSub"}>
            ({props.minLength ? `${props.minLength} -` : null}
            {props.maxLength} {t("common.characters")})
          </Text>
        ) : null}
      </Flex>
      <StyledTextarea
        style={merge(
          props.style,
          props.disabled ? { opacity: "0.2" } : {},
          props.valid === false ? { border: `1px solid ${theme.danger}` } : {},
          {
            border: props.border,
            borderRadius: props.borderRadius,
            placeholder: props.placeholder,
            padding: props.padding || Spacing.s,
            margin: props.margin,
            maxHeight: props.maxHeight,
          },
        )}
        dataTestId={props.dataTestId}
        rows={props.rows}
        // Free text can be typed in either script regardless of the UI
        // language, so follow the content (same as RegularInput).
        dir="auto"
        {...props}
      />

      {!props.valid && props.errorMessage ? (
        <Text color="danger" fontSize="s">
          {props.errorMessage}
        </Text>
      ) : null}
    </Flex>
  );
};
